import _ from 'lodash';
import { useRootStore } from './index';
import { Optional } from 'utils/Optional';
import { findFacebookPage } from 'utils/facebookPages';
import { action, autorun, computed, observable } from 'mobx';
import { CreativeVideo } from '../routes/ArticleGroup/CreativeTemplates/./CreativeTemplatesBuilder/types';
import { DEFAULT_TAB } from 'routes/ArticleGroup/CreativeTemplates/CreativeTemplatesBuilder/utils/TabsDef';
import { generateCreativeTemplateCombinations } from '../routes/ArticleGroup/CreativeTemplates/./CreativeTemplatesBuilder/utils/generateCreativeTemplateCombinations';
import {
  AdFormatEnum,
  CreativeTemplateEditorQuery_articleGroup as ArticleGroup,
  CreativeTemplateEditorQuery_articleGroup_articles_campaign as Campaign,
  JobsCreativesCreateJob_jobsCreativesCreateJob as CreateCreativeJob,
  VideoSourceEnum
} from 'api';
import {
  CarouselCreative,
  CarouselSlide,
  Combinations,
  CreativeImage,
  CreativeTemplateInputs,
  Tab,
  TextAsset
} from 'routes/ArticleGroup/CreativeTemplates/CreativeTemplatesBuilder/types';
import {
  CampaignObjectivesEnum,
  CreativeTemplatesEntities,
  ImageTabs,
  Views
} from '../routes/ArticleGroup/CreativeTemplates/./CreativeTemplatesBuilder/constants';

const { IMAGES, VIDEOS, PostTexts, Headlines, Descriptions, CTAs } = CreativeTemplatesEntities;

export class CreativeTemplatesBuilderStore {
  /* ------------------------------ JOB ------------------------------ */
  @observable jobId: string | undefined = undefined;
  @action resetJobId = () => (this.jobId = undefined);
  @action setJobId = (id: string | undefined) => (this.jobId = id);

  @observable createCreativeInProgress = false;
  @observable setCreateCreativeInProgress = (value: boolean) => (this.createCreativeInProgress = value);

  @observable createCreativeJobResult: CreateCreativeJob | null = null;
  @action resetCreateCreativeJobResult = () => (this.createCreativeJobResult = null);
  @action setCreateCreativeJobResult = (result: CreateCreativeJob) => (this.createCreativeJobResult = result);

  /* ------------------------------ IMAGES ------------------------------ */
  @observable images: CreativeImage[] = [];
  @action setImages = (images: CreativeImage[]) => (this.images = images);

  @observable isImagesLoading = false;
  @action setIsImagesLoading = (value: boolean) => (this.isImagesLoading = value);

  @action preSelectImages = (images: CreativeImage[]) => {
    this.imageOrVideoCreativeTemplateInputs = { ...this.imageOrVideoCreativeTemplateInputs, [IMAGES]: images };
  };

  @action selectImage = (image: CreativeImage, checked: boolean) => {
    const rawImages = this.imageOrVideoCreativeTemplateInputs[IMAGES];
    const filtered = rawImages.filter(({ id, type }) => (id === image.id ? type !== image.type : true));
    const newImages = checked ? [...rawImages, image] : filtered;

    this.imageOrVideoCreativeTemplateInputs = { ...this.imageOrVideoCreativeTemplateInputs, [IMAGES]: newImages };
  };

  @observable isCreativeImagesPreselected = false;
  @action setIsCreativeImagesPreselected = (value: boolean) => (this.isCreativeImagesPreselected = value);

  @observable isArticleImagesPreselected = false;
  @action setIsArticleImagesPreselected = (value: boolean) => (this.isArticleImagesPreselected = value);

  /* ------------------------------ VIDEOS ------------------------------ */
  @observable videos: CreativeVideo[] = [];
  @action setVideos = (videos: CreativeVideo[]) => (this.videos = videos);

  @action preSelectVideos = (videos: string[]) => {
    const newVideos = videos.map((id) => this.videos.find((video) => id === video.id));
    this.imageOrVideoCreativeTemplateInputs = {
      ...this.imageOrVideoCreativeTemplateInputs,
      [VIDEOS]: _.compact(newVideos)
    };
  };

  @action selectVideo = (video: CreativeVideo, checked: boolean) => {
    const rawVideos = this.imageOrVideoCreativeTemplateInputs[VIDEOS];
    const filtered = rawVideos.filter(({ id }) => id !== video.id);
    const newVideo = checked ? [...rawVideos, video] : filtered;

    this.imageOrVideoCreativeTemplateInputs = { ...this.imageOrVideoCreativeTemplateInputs, [VIDEOS]: newVideo };
  };

  @observable isVideoPreselected = false;
  @action setIsVideoPreselected = (value: boolean) => (this.isCreativeImagesPreselected = value);

  /* ------------------------------ CAROUSELS ------------------------------ */
  @observable carousels: CarouselCreative[] = [];
  @action setCarousels = (carousels: CarouselCreative[]) => (this.carousels = carousels);

  @action addNewCarousel = (carousel: CarouselCreative) => this.carousels.push(carousel);

  @action removeCarousel = (id: string) => {
    const carousels = [...this.carousels];
    _.remove(carousels, { id: id });
    this.carousels = carousels;
  };

  @observable activeSlideId = '';
  @action setActiveSlideId = (id: string) => (this.activeSlideId = id);

  @observable activeCarouselId = '';
  @action setActiveCarouselId = (id: string) => (this.activeCarouselId = id);

  @action addNewSlide = (id: string, slide: CarouselSlide) => {
    this.carousels = _.map(this.carousels, (carousel) =>
      carousel.id === id ? { ...carousel, carouselAssetSpecs: [...carousel.carouselAssetSpecs!, slide] } : carousel
    );
  };

  @action removeSlide = (carouselId: string, slideId: string) => {
    const carousels = [...this.carousels];
    const carousel = _.find(carousels, { id: carouselId });
    _.remove(carousel?.carouselAssetSpecs as CarouselSlide[], { id: slideId });
    this.carousels = carousels;
  };

  /* ------------------------------ TEXT ASSETS ------------------------------ */
  @observable isTextAssetsPreselected = false;
  @action setIsTextAssetsPreselected = (value: boolean) => (this.isTextAssetsPreselected = value);

  @action preSelectTextAssets = (textAssets: TextAsset[], tabKey: CreativeTemplatesEntities) => {
    this.imageOrVideoCreativeTemplateInputs = { ...this.imageOrVideoCreativeTemplateInputs, [tabKey]: textAssets };
  };

  @action selectTextAsset = (textAsset: TextAsset) => {
    const activeTabKey = this.activeTab.key;
    const textAssets = [...(this.imageOrVideoCreativeTemplateInputs[activeTabKey] as TextAsset[])];

    const isSelected = textAssets.find(({ id }) => id === textAsset.id);
    const filtered = textAssets.filter(({ id }) => id !== textAsset.id);

    this.imageOrVideoCreativeTemplateInputs = {
      ...this.imageOrVideoCreativeTemplateInputs,
      [activeTabKey]: isSelected ? filtered : [...textAssets, textAsset]
    };
  };

  @action addTextAsset = (assetType: CreativeTemplatesEntities, textAsset: TextAsset) => {
    if (!this.articleGroup) return;

    switch (assetType) {
      case Headlines:
        // @ts-ignore
        this.articleGroup.headlines = [...this.articleGroup.headlines, textAsset];
        break;
      case PostTexts:
        // @ts-ignore
        this.articleGroup.postTexts = [...this.articleGroup.postTexts, textAsset];
        break;
      case Descriptions:
        // @ts-ignore
        this.articleGroup.descriptions = [...this.articleGroup.descriptions, textAsset];
        break;
    }
  };

  @action removeTextAsset = (assetType: CreativeTemplatesEntities, textAsset: TextAsset) => {
    if (!this.articleGroup || assetType === IMAGES || assetType === VIDEOS) return;

    switch (assetType) {
      case Headlines:
        this.articleGroup.headlines = this.articleGroup.headlines.filter(
          (_textAsset) => _textAsset.id !== textAsset.id
        );
        break;
      case PostTexts:
        this.articleGroup.postTexts = this.articleGroup.postTexts.filter(
          (_textAsset) => _textAsset.id !== textAsset.id
        );
        break;
      case Descriptions:
        this.articleGroup.descriptions = this.articleGroup.descriptions.filter(
          (_textAsset) => _textAsset.id !== textAsset.id
        );
        break;
    }

    this.imageOrVideoCreativeTemplateInputs[assetType] = this.imageOrVideoCreativeTemplateInputs[assetType].filter(
      (_textAsset) => _textAsset.id !== textAsset.id
    );
  };

  /* ------------------------------ DIALOGS ------------------------------ */
  @observable openCreateCreativesResultDialog = false;
  @action setOpenCreateCreativesResultDialog = (value: boolean) => (this.openCreateCreativesResultDialog = value);

  @observable openImageUploaderDialog = false;
  @action setOpenImageUploaderDialog = (value: boolean) => (this.openImageUploaderDialog = value);

  @observable openVideoUploaderDialog = false;
  @action setOpenVideoUploaderDialog = (value: boolean) => (this.openVideoUploaderDialog = value);

  /* ------------------------------ TABS ------------------------------ */
  @observable view: Views = Views.ImageOrVideoCreativeTemplateEditor;
  @action setView = (view: Views) => (this.view = view);

  @observable activeTab: Tab = DEFAULT_TAB;
  @action setActiveTab = (newTab: Tab) => (this.activeTab = newTab);

  @observable activeImageTab: ImageTabs = ImageTabs.MARKETER;
  @action setActiveImageTab = (tab: ImageTabs) => (this.activeImageTab = tab);

  @observable activeVideoTab: VideoSourceEnum = VideoSourceEnum.ACCOUNT;
  @action setActiveVideoTab = (tab: VideoSourceEnum) => (this.activeVideoTab = tab);

  /* ------------------------------ CAMPAIGN ------------------------------ */
  @computed get campaigns(): Campaign[] {
    if (!this.articleGroup) return [];

    return this.articleGroup.articles
      .flatMap(({ campaign }) => campaign)
      .filter((value, index, self) => index === self.findIndex(({ id }) => id === value.id));
  }

  @observable campaignObjective: CampaignObjectivesEnum = CampaignObjectivesEnum.ALL_CAMPAIGN_OBJECTIVES;
  @action setCampaignObjective = (objective: CampaignObjectivesEnum) => (this.campaignObjective = objective);

  @observable selectedCampaign: Optional<Campaign> = null;
  @action setSelectedCampaign = (campaign: Campaign | null) => {
    this.selectedCampaign = campaign;
    this.previewArticleId = this.previewArticleOptions[0]?.id ?? null;

    this.resetCombinationsFacebookPreviews();
  };

  /* ------------------------------ PREVIEW ARTICLE ------------------------------ */
  // Page-Locked Articles: previews of an enforced campaign render on one article's Facebook Page.
  @computed get previewArticleOptions() {
    const campaign = this.selectedCampaign;
    if (!campaign?.pageLockedArticlesEnabled) return [];
    return this.articles.filter((article) => article.campaign.id === campaign.id && !article.deprecated);
  }

  @observable previewArticleId: string | null = null;
  @action setPreviewArticleId = (articleId: string | null) => {
    this.previewArticleId = articleId;
    this.resetCombinationsFacebookPreviews();
  };

  /* ------------------------------ ARTICLE GROUP ------------------------------ */
  @observable articleGroup: Optional<ArticleGroup> = null;
  @action setArticleGroup = (articleGroup: ArticleGroup) => {
    this.articleGroup = articleGroup;

    const notDeprecatedArticles = this.articleGroup.articles.filter((article) => !article.deprecated);
    const articleIds = notDeprecatedArticles.map((article) => article.id);
    this.setSelectedArticleIds(articleIds);

    const campaign = articleGroup.articles[0]?.campaign;
    this.setSelectedCampaign(campaign);

    this.setSelectedCampaignFacebookPageIds(articleGroup);
  };

  @computed get articleGroupAssets(): Optional<{ [key: string]: TextAsset[] }> {
    if (!this.articleGroup) return null;

    return {
      [Headlines]: _.orderBy(
        this.articleGroup.headlines,
        (headline) => {
          const engagementScore = (headline.postMaloneScore?.engagementScore || 0) / 100;
          return [headline.isBlank, headline.isAutofilled, engagementScore];
        },
        ['desc', 'desc', 'asc']
      ),
      [PostTexts]: _.orderBy(
        this.articleGroup.postTexts,
        (postText) => {
          const engagementScore = (postText.postMaloneScore?.engagementScore || 0) / 100;
          return [postText.isBlank, engagementScore];
        },
        ['desc', 'asc']
      ),
      [Descriptions]: _.orderBy(
        this.articleGroup.descriptions,
        (headline) => {
          return [headline.isBlank, headline.isAutofilled];
        },
        ['desc', 'desc']
      )
    };
  }

  /* ------------------------------ ARTICLES ------------------------------ */
  @computed get articles() {
    return this.articleGroup?.articles || [];
  }

  @observable selectedArticleIds: string[] = [];
  @action setSelectedArticleIds = (articleIds: string[]) => (this.selectedArticleIds = _.uniq(articleIds));

  @action addSelectedArticleId = (articleId: string) => {
    this.setSelectedArticleIds([...this.selectedArticleIds, articleId]);
  };

  @action removeSelectedArticleId = (articleId: string) => {
    this.selectedArticleIds = this.selectedArticleIds.filter((id) => id !== articleId);
  };

  /* ------------------------------ OTHER ------------------------------ */
  @observable selectedLeadgenFormPerArticle: { [Identifier: string]: string } = {};

  @observable selectedCampaignFacebookPageIds: { [Identifier: string]: string[] } = {};
  @action setSelectedCampaignFacebookPageIds = (articleGroup: ArticleGroup) => {
    const selectedCampaignFacebookPageIds: { [Identifier: string]: string[] } = {};

    articleGroup.articles.forEach((article) => {
      const pages = article.campaign.campaignFacebookPages;
      // Page-Locked Articles: only the article's own page; an enforced article without one sends [] and the job
      // fails it with a clear message.
      if (article.campaign.pageLockedArticlesEnabled) {
        const page = findFacebookPage(pages, article.facebookPageId);
        selectedCampaignFacebookPageIds['' + article.id] = page ? ['' + page.id] : [];
        return;
      }
      selectedCampaignFacebookPageIds['' + article.id] = pages.map(
        (campaignFacebookPage) => '' + campaignFacebookPage.id
      );
    });

    this.selectedCampaignFacebookPageIds = selectedCampaignFacebookPageIds;
  };

  @observable creativeTemplateCombinations: Combinations = [];
  @action setCreativeTemplateCombinations = (previews: Combinations) => (this.creativeTemplateCombinations = previews);

  @observable imageOrVideoCreativeTemplateInputs: CreativeTemplateInputs = {
    [IMAGES]: [],
    [VIDEOS]: [],
    [Headlines]: [],
    [PostTexts]: [],
    [Descriptions]: [],
    [CTAs]: []
  };

  @action resetSelections = (activeTabKey: CreativeTemplatesEntities) => {
    this.imageOrVideoCreativeTemplateInputs = { ...this.imageOrVideoCreativeTemplateInputs, [activeTabKey]: [] };
  };

  @observable selectedPreviewFormat: AdFormatEnum = AdFormatEnum.MOBILE_FEED_STANDARD;
  @action setSelectedPreviewFormat = (format: AdFormatEnum) => {
    this.selectedPreviewFormat = format;

    this.resetCombinationsFacebookPreviews();
  };
  @action resetCombinationsFacebookPreviews = () => {
    const creativeTemplateCombinations = this.creativeTemplateCombinations.map((combination) => ({
      ...combination,
      facebookPreviewIframe: null
    }));

    this.setCreativeTemplateCombinations(creativeTemplateCombinations);
  };

  @computed get carouselSelections() {
    return _.reduce(
      this.carousels,
      (acc, currentValue) => {
        const currentImages = _.map(currentValue.carouselAssetSpecs, 'image') as CreativeImage[];
        const currentVideos = _.map(currentValue.carouselAssetSpecs, 'video') as CreativeVideo[];
        const currentHeadlines = _.map(currentValue.carouselAssetSpecs, 'textAssets.headline');
        const currentDescriptions = _.map(currentValue.carouselAssetSpecs, 'textAssets.description');

        const images = _.concat(acc[CreativeTemplatesEntities.IMAGES], currentImages);
        const videos = _.concat(acc[CreativeTemplatesEntities.VIDEOS], currentVideos);
        const headlines = _.concat(acc[CreativeTemplatesEntities.Headlines], currentHeadlines);
        const descriptions = _.concat(acc[CreativeTemplatesEntities.Descriptions], currentDescriptions);

        currentValue.callToAction && acc[CreativeTemplatesEntities.CTAs].push(currentValue.callToAction);
        currentValue.message && acc[CreativeTemplatesEntities.PostTexts].push(currentValue.message);

        return {
          [CreativeTemplatesEntities.CTAs]: _.uniqBy(acc.ctas, 'id'),
          [CreativeTemplatesEntities.IMAGES]: _.compact(_.uniqBy(images, 'id')),
          [CreativeTemplatesEntities.VIDEOS]: _.compact(_.uniqBy(videos, 'id')),
          [CreativeTemplatesEntities.Headlines]: _.compact(_.uniqBy(headlines, 'id')),
          [CreativeTemplatesEntities.Descriptions]: _.compact(_.uniqBy(descriptions, 'id')),
          [CreativeTemplatesEntities.PostTexts]: _.uniqBy(acc[CreativeTemplatesEntities.PostTexts], 'id')
        };
      },
      {
        [CreativeTemplatesEntities.IMAGES]: [] as CreativeImage[],
        [CreativeTemplatesEntities.VIDEOS]: [] as CreativeVideo[],
        [CreativeTemplatesEntities.PostTexts]: [] as TextAsset[],
        [CreativeTemplatesEntities.Headlines]: [] as TextAsset[],
        [CreativeTemplatesEntities.Descriptions]: [] as TextAsset[],
        [CreativeTemplatesEntities.CTAs]: [] as TextAsset[]
      }
    );
  }

  reactToCreativeTemplateInputsChange = autorun(() => {
    if (!this.articleGroup) return;

    const combinations = generateCreativeTemplateCombinations(
      this.articleGroup,
      this.imageOrVideoCreativeTemplateInputs,
      this.carousels
    );
    this.setCreativeTemplateCombinations(combinations);
  });
}

export const useCreativeTemplatesBuilderStore = (): CreativeTemplatesBuilderStore => {
  const store = useRootStore();
  if (!store.creativeTemplatesBuilderStore) throw new Error('CT store has not been initialized');
  return store.creativeTemplatesBuilderStore;
};
